import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getBlogs, createBlog, updateBlog, deleteBlog } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, Eye, EyeOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { format } from 'date-fns';

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image: '',
    author: 'Wizbang Team',
    tags: '',
    published: true,
  });

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      const res = await getBlogs(false);
      setBlogs(res.data);
    } catch (err) {
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: editingBlog ? formData.slug : generateSlug(title),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
    };

    try {
      if (editingBlog) {
        await updateBlog(editingBlog.id, data);
        toast.success('Blog post updated');
      } else {
        await createBlog(data);
        toast.success('Blog post created');
      }
      loadBlogs();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save blog post');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      slug: blog.slug,
      excerpt: blog.excerpt,
      content: blog.content,
      image: blog.image,
      author: blog.author,
      tags: blog.tags?.join(', ') || '',
      published: blog.published,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      await deleteBlog(id);
      toast.success('Blog post deleted');
      loadBlogs();
    } catch (err) {
      toast.error('Failed to delete blog post');
    }
  };

  const resetForm = () => {
    setEditingBlog(null);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image: '',
      author: 'Wizbang Team',
      tags: '',
      published: true,
    });
  };

  return (
    <AdminLayout title="Blog Management">
      <div className="space-y-6" data-testid="admin-blogs">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">Blog Posts</h1>
            <p className="text-[#888] text-sm">Create and manage blog content</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button
                data-testid="add-blog-btn"
                className="bg-[#ff5a1f] text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-[#e04e1a] transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Post
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-syne text-xl">
                  {editingBlog ? 'Edit Blog Post' : 'New Blog Post'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="blog-form">
                <div>
                  <label className="block text-[#888] text-sm mb-2">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="Your blog post title"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="your-blog-post-url"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Excerpt *</label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    required
                    rows={2}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none resize-none"
                    placeholder="Brief description of the post"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Content *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows={8}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none resize-none"
                    placeholder="Write your blog post content here..."
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Image URL *</label>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      required
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-[#888] text-sm mb-2">Author</label>
                    <input
                      type="text"
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Tags (comma separated)</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="marketing, growth, strategy"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="published"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    className="w-4 h-4 accent-[#ff5a1f]"
                  />
                  <label htmlFor="published" className="text-white text-sm">Published</label>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1 border border-[#333] text-white px-4 py-3 text-sm font-semibold hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#ff5a1f] text-white px-4 py-3 text-sm font-semibold hover:bg-[#e04e1a] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingBlog ? 'Update' : 'Publish'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Blog List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
          </div>
        ) : blogs.length > 0 ? (
          <div className="space-y-4">
            {blogs.map((blog) => (
              <div
                key={blog.id}
                className="bg-[#0A0A0A] border border-[#1F1F1F] p-4 flex gap-4"
                data-testid={`blog-item-${blog.id}`}
              >
                <div className="w-32 h-20 flex-shrink-0 bg-[#121212] overflow-hidden">
                  <img src={blog.image} alt={blog.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-syne font-bold text-white truncate">{blog.title}</h3>
                      <p className="text-[#888] text-sm line-clamp-1">{blog.excerpt}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {blog.published ? (
                        <span className="text-green-500 text-xs flex items-center gap-1">
                          <Eye className="w-3 h-3" /> Published
                        </span>
                      ) : (
                        <span className="text-yellow-500 text-xs flex items-center gap-1">
                          <EyeOff className="w-3 h-3" /> Draft
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-[#555] text-xs">
                      {blog.created_at && format(new Date(blog.created_at), 'MMM d, yyyy')}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(blog)}
                        className="text-[#888] hover:text-[#ff5a1f] p-1"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(blog.id)}
                        className="text-[#888] hover:text-red-500 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-16 text-center">
            <p className="text-[#888] mb-4">No blog posts yet</p>
            <button
              onClick={() => setDialogOpen(true)}
              className="text-[#ff5a1f] font-semibold hover:underline"
            >
              Write your first post
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
